import { PrimeReactButton } from 'components/common';
import { automationErrorLogCodes } from 'constants/automationConstants';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { convertToTitleCase } from 'utils/stringCaseFormattingUtils';
import { handleDateTimeOffset } from 'utils/utility_functions/timezone';

export const initialFilterValues = {
  case: [],
  lead: [],
  errorCode: [],
  createdDate: [],
  status: '',
};

const ERROR_DESC = 'errorDesc';
const CASE_LEAD = 'caseLead';
const ACTION_NAME = 'actionName';
const CREATED_DATE_TIME = 'createdDateTime';
const LAST_UPDATED = 'lastUpdated';
const STATUS = 'status';
const ACTION = 'action';

const { eventTimePassed } = automationErrorLogCodes || {};

const displayFormattedDateTimeWithTimeOffset = (params) => {
  const { dateTimeString, hourFormat, month_date_year_format, month_date_year_time_12_format, timeZone } = params || {};

  let dateFormat = month_date_year_format;
  let formatteDateTimeString = 'N/A';
  if (hourFormat === 12) {
    dateFormat = month_date_year_time_12_format;
  }
  if (dateTimeString) {
    formatteDateTimeString = handleDateTimeOffset(timeZone, dateTimeString, dateFormat);
  }
  return formatteDateTimeString;
};

/** ------------------------ Template section starts ------------------ **/

const errorDescriptionTemplate = (rowData) => {
  return (
    <>
      <span className="p-column-title text-break">Error Description</span>
      <span className="text-bold" title={rowData?.error_message || undefined}>
        {rowData?.error_message || 'N/A'}
      </span>
    </>
  );
};

const caseNameOrLeadNameTemplate = (rowData) => {
  let url = '/';

  const { is_lead = false, case_id = '', status = '' } = rowData || {};

  //case_name and case_id  keys are used for both "case" and "lead" modules

  const lowerCasedStatusValue = status.toLowerCase();

  const blockRedirection = ['case deleted', 'lead deleted']?.includes(lowerCasedStatusValue);

  if (is_lead) {
    url = `/leads/${case_id}`;
  } else {
    url = `/cases/${case_id}`;
  }

  return (
    <>
      <span className="p-column-title text-break">Case/Lead Name</span>
      <Link to={url} style={{ textDecoration: 'none' }}>
        <span
          className={`${!blockRedirection ? 'cursor-pointer' : ''}  text-primary-dark text-decoration-none`}
          title={rowData?.case_name || undefined}
        >
          {rowData?.case_name || 'N/A'}
        </span>
      </Link>
      <div className="text-hint" title={is_lead ? 'Lead' : 'Case'}>
        {is_lead ? 'Lead' : 'Case'}
      </div>
    </>
  );
};

const actionNameTemplate = (rowData) => {
  return (
    <>
      <span className="p-column-title text-break">Action Name</span>
      <span title={rowData?.action_label || undefined}>{rowData?.action_label || 'N/A'}</span>
    </>
  );
};

const createdDateAndTimeTemplate = (params) => {
  return (
    <>
      <span className="p-column-title text-break">Created Date and Time</span>
      <span className="text-break" title={displayFormattedDateTimeWithTimeOffset(params)}>
        {displayFormattedDateTimeWithTimeOffset(params)}
      </span>
    </>
  );
};
const lastUpdatedTemplate = (params) => {
  return (
    <>
      <span className="p-column-title text-break">Last Updated</span>
      <span className="text-break" title={displayFormattedDateTimeWithTimeOffset(params)}>
        {displayFormattedDateTimeWithTimeOffset(params)}
      </span>
    </>
  );
};

const statusTemplate = (rowData) => {
  return (
    <>
      <span className="p-column-title ">Status</span>
      <span className={`px-2 ${rowData?.status === 'open' ? 'red' : 'green'}-tag`} aria-controls="active_popup_menu">
        <span
          className="p-tag-"
          title={
            convertToTitleCase({
              str: rowData?.status,
              separator: ' ',
            }) || undefined
          }
        >
          {rowData?.status === 'in progress' && <i class="fas fa-spinner fa-pulse me-1"></i>}

          {
            // rowData?.status || "N/A"
            convertToTitleCase({
              str: rowData?.status,
              separator: ' ',
            }) || 'N/A'
          }
        </span>
      </span>
    </>
  );
};

const actionTemplate = (rowData, handleFixActionButtonClick) => {
  const { status, error_code } = rowData || {};

  let isDisabled = false;

  let isErrorFixed = status !== 'open';

  let isEventTimePassed = error_code === eventTimePassed;

  isDisabled = isErrorFixed || isEventTimePassed;

  return (
    <>
      <span className="p-column-title text-break">Actions</span>

      <PrimeReactButton
        className="cursor-pointer p-button p-button-secondary fix-button"
        aria-controls="active_popup_menu"
        onButtonClick={(event) => {
          handleFixActionButtonClick(rowData);
        }}
        isDisabled={isDisabled}
      >
        Fix
      </PrimeReactButton>
    </>
  );
};

/** ------------------------- Template section ends -------------------------- **/

const renderTemplate = (props) => {
  const { key, rowData, handleFixActionButtonClick, month_date_year_format, month_date_year_time_12_format, timeZone, history } =
    props || {};

  let columnTemplate;
  let dateTimeParams = {
    hourFormat: 12,
    month_date_year_format,
    month_date_year_time_12_format,
    timeZone,
  };

  switch (key) {
    case ERROR_DESC:
      columnTemplate = errorDescriptionTemplate(rowData);
      break;
    case CASE_LEAD:
      columnTemplate = caseNameOrLeadNameTemplate(rowData);

      break;
    case ACTION_NAME:
      columnTemplate = actionNameTemplate(rowData);

      break;
    case CREATED_DATE_TIME:
      dateTimeParams['dateTimeString'] = rowData?.created || new Date();
      columnTemplate = createdDateAndTimeTemplate(dateTimeParams);

      break;
    case LAST_UPDATED:
      dateTimeParams['dateTimeString'] = rowData?.updated || new Date();

      columnTemplate = lastUpdatedTemplate(dateTimeParams);

      break;
    case STATUS:
      columnTemplate = statusTemplate(rowData);

      break;
    case ACTION:
      columnTemplate = actionTemplate(rowData, handleFixActionButtonClick);

      break;
    default:
      break;
  }

  return columnTemplate;
};

export const generateAutomationErrorLogDataTableColumns = (props) => {
  const automationErrorLogDataTableColumnsData = [
    {
      header: 'Error Description',
      field: 'error_message',
      body: (rowData) => renderTemplate({ key: ERROR_DESC, rowData, ...props }),
      sortable: true,
    },
    {
      header: 'Case/Lead Name',
      field: 'case_name',
      body: (rowData) => renderTemplate({ key: CASE_LEAD, rowData, ...props }),
      sortable: true,
    },
    {
      header: 'Action Name',
      field: 'action_label',
      body: (rowData) => renderTemplate({ key: ACTION_NAME, rowData, ...props }),
      sortable: true,
    },
    {
      header: 'Date and Time',
      field: 'created',
      body: (rowData) => renderTemplate({ key: CREATED_DATE_TIME, rowData, ...props }),
      sortable: true,
    },
    {
      header: 'Last Updated',
      field: 'updated',
      body: (rowData) => renderTemplate({ key: LAST_UPDATED, rowData, ...props }),
      sortable: true,
    },
    {
      header: 'Status',
      field: 'status',
      body: (rowData) => renderTemplate({ key: STATUS, rowData, ...props }),
      sortable: true,
      style: { width: '200px' },
    },
    {
      header: 'Action',
      field: 'action',
      body: (rowData) => renderTemplate({ key: ACTION, rowData, ...props }),
      sortable: true,
      style: { width: '160px' },
    },
  ];

  return automationErrorLogDataTableColumnsData;
};
